import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useScrollTrigger,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";

const navItems = [
  { label: "首页", href: "#home" },
  { label: "功能特色", href: "#features" },
  { label: "使用场景", href: "#scenarios" },
  { label: "关于我们", href: "#about" },
];

function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 10,
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavClick = (href) => {
    setMobileOpen(false);
    const element = document.querySelector(href);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  // 移动端抽屉菜单
  const drawer = (
    <Box
      sx={{
        width: 300,
        height: "100%",
        background: "linear-gradient(180deg, #0a0a0f 0%, #12121a 100%)",
        color: "#f8fafc",
      }}
    >
      {/* 抽屉头部 */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2.5,
          borderBottom: "1px solid rgba(0, 212, 255, 0.1)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 0 20px rgba(0, 212, 255, 0.3)",
            }}
          >
            <AutoStoriesIcon sx={{ color: "#0a0a0f", fontSize: 22 }} />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              background: "linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            知嘛
          </Typography>
        </Box>
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            color: "#94a3b8",
            "&:hover": {
              bgcolor: "rgba(0, 212, 255, 0.1)",
              color: "#00d4ff",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* 导航列表 */}
      <List sx={{ pt: 2 }}>
        {navItems.map((item) => (
          <ListItem
            key={item.label}
            onClick={() => handleNavClick(item.href)}
            sx={{
              py: 1.5,
              px: 3,
              cursor: "pointer",
              transition: "all 0.3s ease",
              borderLeft: "3px solid transparent",
              "&:hover": {
                bgcolor: "rgba(0, 212, 255, 0.05)",
                borderLeftColor: "#00d4ff",
                "& .MuiListItemText-primary": {
                  color: "#00d4ff",
                },
              },
            }}
          >
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: "1rem",
                fontWeight: 500,
                color: "#e2e8f0",
                transition: "color 0.3s ease",
              }}
            />
          </ListItem>
        ))}
      </List>

      {/* 底部按钮 */}
      <Box sx={{ p: 3, mt: 2 }}>
        <Button
          variant="contained"
          fullWidth
          size="large"
          startIcon={<RocketLaunchIcon />}
          onClick={() => handleNavClick("#download")}
          sx={{
            py: 1.5,
            borderRadius: "12px",
            fontSize: "1rem",
            fontWeight: 600,
            color: "#0a0a0f",
            background:
              "linear-gradient(135deg, #00d4ff 0%, #0099ff 50%, #7c3aed 100%)",
            boxShadow:
              "0 0 30px rgba(0, 212, 255, 0.3), 0 4px 15px rgba(0, 212, 255, 0.2)",
            "&:hover": {
              background:
                "linear-gradient(135deg, #00d4ff 0%, #0099ff 50%, #7c3aed 100%)",
              boxShadow:
                "0 0 40px rgba(0, 212, 255, 0.5), 0 6px 20px rgba(0, 212, 255, 0.3)",
            },
          }}
        >
          立即体验
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: trigger ? "rgba(10, 10, 15, 0.95)" : "rgba(10, 10, 15, 0.8)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderBottom: trigger
            ? "1px solid rgba(0, 212, 255, 0.15)"
            : "1px solid rgba(255, 255, 255, 0.05)",
          transition: "all 0.3s ease",
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            sx={{
              py: 1,
              justifyContent: "space-between",
              minHeight: { xs: 64, md: 72 },
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  "& .logo-box": {
                    boxShadow: "0 0 30px rgba(0, 212, 255, 0.5)",
                    transform: "scale(1.05)",
                  },
                },
              }}
              onClick={() => handleNavClick("#home")}
            >
              <Box
                className="logo-box"
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "12px",
                  background:
                    "linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 0 20px rgba(0, 212, 255, 0.3)",
                  transition: "all 0.3s ease",
                }}
              >
                <AutoStoriesIcon sx={{ color: "#0a0a0f", fontSize: 24 }} />
              </Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                  background:
                    "linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: "-0.02em",
                }}
              >
                知嘛
              </Typography>
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {navItems.map((item) => (
                  <Button
                    key={item.label}
                    onClick={() => handleNavClick(item.href)}
                    sx={{
                      px: 2.5,
                      py: 1,
                      borderRadius: "10px",
                      fontSize: "0.95rem",
                      fontWeight: 500,
                      color: "#e2e8f0",
                      textTransform: "none",
                      position: "relative",
                      overflow: "hidden",
                      transition: "all 0.3s ease",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        bottom: 6,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 0,
                        height: 2,
                        background: "linear-gradient(90deg, #00d4ff, #7c3aed)",
                        borderRadius: 1,
                        transition: "width 0.3s ease",
                      },
                      "&:hover": {
                        bgcolor: "rgba(0, 212, 255, 0.08)",
                        color: "#00d4ff",
                        "&::before": {
                          width: "60%",
                        },
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
                <Button
                  variant="contained"
                  startIcon={<RocketLaunchIcon sx={{ fontSize: 18 }} />}
                  onClick={() => handleNavClick("#download")}
                  sx={{
                    ml: 2,
                    px: 3,
                    py: 1,
                    borderRadius: "10px",
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    textTransform: "none",
                    color: "#0a0a0f",
                    background:
                      "linear-gradient(135deg, #00d4ff 0%, #0099ff 50%, #7c3aed 100%)",
                    boxShadow:
                      "0 0 20px rgba(0, 212, 255, 0.3), 0 4px 12px rgba(0, 212, 255, 0.2)",
                    border: "1px solid rgba(0, 212, 255, 0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #00d4ff 0%, #0099ff 50%, #7c3aed 100%)",
                      boxShadow:
                        "0 0 30px rgba(0, 212, 255, 0.5), 0 6px 20px rgba(0, 212, 255, 0.3)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  立即体验
                </Button>
              </Box>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                onClick={handleDrawerToggle}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "10px",
                  color: "#e2e8f0",
                  bgcolor: "rgba(0, 212, 255, 0.1)",
                  border: "1px solid rgba(0, 212, 255, 0.2)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    bgcolor: "rgba(0, 212, 255, 0.2)",
                    borderColor: "rgba(0, 212, 255, 0.4)",
                    color: "#00d4ff",
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          sx: {
            boxShadow: "-10px 0 40px rgba(0, 0, 0, 0.5)",
            borderLeft: "1px solid rgba(0, 212, 255, 0.1)",
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Toolbar spacer */}
      <Toolbar sx={{ minHeight: { xs: 64, md: 72 } }} />
    </>
  );
}

export default Header;
